import cn from 'classnames'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import './coreTabs.scss'

interface ITab {
  label: string
  value: string
  disabled?: boolean
  badge?: number
}

interface ICoreTabs {
  onChangeTab: (value: any) => void
  tabs: ITab[]
  tabState?: string
  disabled?: boolean
  initial?: string
}

export const CoreTabs = ({ tabs, onChangeTab, tabState, disabled = false, initial }: ICoreTabs) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(null)
  const [backgroundLeftSide, setUnderlineLeft] = useState(0)
  const [backgroundWidth, setBackgroundWidth] = useState(0)
  const [isHidden, setIsHidden] = useState(true)
  const [animateUnderline, setAnimateUnderline] = useState(true)
  const selectedRef = useRef<HTMLInputElement>(null)
  const initialRef = useRef<HTMLInputElement>(null)

  const getTextWidth = (el: HTMLElement) => el.querySelector('p')?.offsetWidth ?? el.offsetWidth

  useEffect(() => {
    setSelectedTabIndex(() => {
      setIsHidden(false)
      return tabs.indexOf(tabs.find((tab: ITab) => tab.value === tabState))
    })
    if (selectedRef.current) {
      setBackgroundWidth(getTextWidth(selectedRef.current))
      setUnderlineLeft(selectedRef.current.offsetLeft + Math.round(selectedRef.current.offsetWidth / 2))
      setIsHidden(false)
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }, [tabState, selectedTabIndex])

  useEffect(() => {
    if (initialRef.current) {
      setBackgroundWidth(getTextWidth(initialRef.current))
      setUnderlineLeft(initialRef.current.offsetLeft + Math.round(initialRef.current.offsetWidth / 2))
      setIsHidden(false)
    }
  }, [initial])

  // Labels and badges change widths (e.g. a count appearing), which shifts the tabs after them.
  // Re-measure before paint and snap the underline there — sliding would only show it lagging.
  const layoutKey = tabs.map(tab => `${tab.label}:${tab.badge ?? ''}`).join('|')
  const lastLayoutKeyRef = useRef(layoutKey)

  useLayoutEffect(() => {
    if (lastLayoutKeyRef.current === layoutKey) return
    lastLayoutKeyRef.current = layoutKey
    const el = selectedRef.current
    if (!el) return
    setAnimateUnderline(false)
    setBackgroundWidth(getTextWidth(el))
    setUnderlineLeft(el.offsetLeft + Math.round(el.offsetWidth / 2))
    const frame = requestAnimationFrame(() => setAnimateUnderline(true))
    return () => cancelAnimationFrame(frame)
  }, [layoutKey])

  const handleTabClick = e => {
    const tab = e.currentTarget as HTMLElement
    setUnderlineLeft(tab.offsetLeft + Math.round(tab.offsetWidth / 2))
    setBackgroundWidth(getTextWidth(tab))
  }

  return (
    <div data-test='core-tabs-wrapper' className={cn('core-tabs-wrapper', { disabled: disabled })}>
      {tabs.map((tab, index) => {
        return (
          <div
            key={index}
            data-test={`tab-item-${index}-test`}
            className={cn('tab-item', {
              selected: selectedTabIndex === index,
              disabled: tab.disabled
            })}
            ref={tab.value === initial ? initialRef : tab.value === tabState ? selectedRef : null}
            onClick={e => {
              if (!disabled && !tab.disabled) {
                handleTabClick(e)
                setSelectedTabIndex(index)
                onChangeTab(tab.value)
              }
            }}
          >
            <p data-test={`tab-item-label-${tab.label}-test`}>
              {tab.label}
              {tab.badge > 0 && (
                <span className='tab-badge' data-test='pending-approvals-tab-badge'>
                  {tab.badge}
                </span>
              )}
            </p>
          </div>
        )
      })}
      <div
        data-test={`animated-background-test`}
        className='animated-background'
        style={{
          display: isHidden ? 'none' : 'flex',
          ...(animateUnderline ? null : { transition: 'none' }),
          width: `${backgroundWidth}px`,
          left: `${backgroundLeftSide}px`
        }}
      />
    </div>
  )
}
